import React from 'react';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAddToExistEstimates } from '@entities/employee/api/hooks/useAddToExistEstimates';
import { IEmployee, TEmployeeTypeInObjectOrEstimate } from '@entities/employee/model/types';
import { useHandleForm } from '@shared/hooks';
import { CustomModal, Form, Select, UiButton } from '@shared/ui';

import cls from './AddToAllEstimatesModal.module.css';
import { addToAllEstimatesData } from '../../model/data';

interface IAddToAllEstimatesModalProps {
    isOpen: boolean;
    onClose: () => void;
    email: IEmployee['email'];
}

interface IAddToAllEstimatesFormValues {
    addToAllEstimatesPosition: TEmployeeTypeInObjectOrEstimate;
}

export const AddToAllEstimatesModal = ({
    isOpen,
    onClose,
    email,
}: IAddToAllEstimatesModalProps) => {
    const { t } = useTranslation();
    const addToExistEstimatesMutation = useAddToExistEstimates();
    const { control, handleSubmit, isSubmitting } = useHandleForm<IAddToAllEstimatesFormValues>({
        onSubmit: async (formData) => {
            const res = await addToExistEstimatesMutation.mutateAsync({
                role: formData.addToAllEstimatesPosition,
                email,
            });
            if (res?.data.success) {
                onClose();
            }
        },
        defaultValues: {
            addToAllEstimatesPosition: 'manager',
        },
        resetEnable: false,
    });
    return (
        <CustomModal
            isOpen={isOpen}
            onHideModal={onClose}
            contentClasses={cls.Container}
            closeButtonClass={cls.CloseButton}
            isLoading={addToExistEstimatesMutation.isPending}
        >
            <CustomModal.Header classes={cls.CustomModalHeader}>
                {t('AccessSettingsForm.AddToAllEstimatesModal.Header')}
            </CustomModal.Header>
            <CustomModal.Body>
                <Form onSubmit={handleSubmit} id="add-to-all-estimates-form">
                    <Controller
                        key="addToAllEstimatesPosition"
                        name="addToAllEstimatesPosition"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isSmartPosition
                                {...field}
                                options={addToAllEstimatesData}
                                id="addToAllEstimatesSelect"
                                needSearch={false}
                                classes={cls.SelectRoot}
                            />
                        )}
                    />
                </Form>
            </CustomModal.Body>
            <CustomModal.Footer classes={cls.CustomModalFooter}>
                <UiButton
                    btnColor="secondary"
                    btnText={t('AccessSettingsForm.AddToAllEstimatesModal.CancelBtn')}
                    onClick={onClose}
                />
                <UiButton
                    type="submit"
                    form="add-to-all-estimates-form"
                    btnColor="primary"
                    btnText={t('AccessSettingsForm.AddToAllEstimatesModal.SubmitBtn')}
                    disabled={isSubmitting}
                />
            </CustomModal.Footer>
        </CustomModal>
    );
};
